import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
import { S3File } from "../../../../../telemetry/hooks/useGetS3Files";

export type SortMode = "" | "fn_desc" | "fn_asc";

function getFilenameStartMs(filename: string): number {
  try {
    const base = filename.replace(/\.[^/.]+$/, "");
    const parts = base.split("_");

    if (parts.length < 3) return 0;

    parts.pop(); // drop duration

    const startTimeRaw = parts.pop() as string; // HH-mm-ss

    const dateRaw = parts.pop() as string; // YYYY-MM-DD

    // Filenames are in UTC; parse as UTC and convert to local for correct local ordering
    const start = dayjs
      .utc(`${dateRaw} ${startTimeRaw.replace(/-/g, ":")}`)
      .local();

    return start.isValid() ? start.valueOf() : 0;
  } catch {
    return 0;
  }
}

export function sortFilesByMode(
  files: S3File[] | undefined,
  sortMode: SortMode
): S3File[] {
  if (!files) return [];

  const arr = [...files];

  arr.sort((a, b) => {
    if (sortMode === "") {
      const ta = a.lastModified ? new Date(a.lastModified).getTime() : 0;

      const tb = b.lastModified ? new Date(b.lastModified).getTime() : 0;

      if (tb !== ta) return tb - ta;

      const fa = getFilenameStartMs(a.filename);

      const fb = getFilenameStartMs(b.filename);

      return fb - fa;
    }

    const ta = getFilenameStartMs(a.filename);

    const tb = getFilenameStartMs(b.filename);

    return sortMode === "fn_desc" ? tb - ta : ta - tb;
  });

  return arr;
}

type Props = {
  value: SortMode;
  onChange: (mode: SortMode) => void;
};

export function RecordingsSortFilter({ value, onChange }: Props): JSX.Element {
  const isAsc = value === "fn_asc";
  const isActive = value !== "";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        border: theme =>
          `1px solid ${
            isActive
              ? theme.palette.primary.main
              : theme.palette.otherStandardInputLine.main
          }`,
        borderRadius: "10px",
        px: 1.5,
        height: "40px",
        width: "auto",
        cursor: "pointer",
        userSelect: "none",
        backgroundColor: theme =>
          isActive
            ? theme.palette.primary.shades?.["4p"] ?? "transparent"
            : "transparent",
        "&:hover": {
          borderColor: theme => theme.palette.primary.main,
          backgroundColor: theme =>
            isActive
              ? theme.palette.primary.shades?.["8p"] ?? "transparent"
              : theme.palette.primary.shades?.["4p"] ?? "transparent",
        },
      }}
      role="button"
      aria-pressed={isAsc ? "true" : "false"}
      tabIndex={0}
      onClick={() => onChange(value === "fn_desc" ? "fn_asc" : "fn_desc")}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();

          onChange(value === "fn_desc" ? "fn_asc" : "fn_desc");
        }
      }}
    >
      {isAsc ? (
        <ArrowUpwardOutlinedIcon
          fontSize="small"
          sx={{
            color: theme => (isActive ? theme.palette.primary.main : "action"),
          }}
        />
      ) : (
        <ArrowDownwardOutlinedIcon
          fontSize="small"
          sx={{
            color: theme => (isActive ? theme.palette.primary.main : "action"),
          }}
        />
      )}
      <Typography
        variant="body1Regular"
        sx={{
          fontWeight: 500,
          color: theme => (isActive ? theme.palette.primary.main : "inherit"),
        }}
      >
        Sort by time
      </Typography>
    </Box>
  );
}
